import type { OrgBranding } from '@emr/shared';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePrefersDark } from '../hooks/useMediaQuery.ts';
import { resolveAccentSource } from './accentSource.ts';
import { describeThemeCheck, verifyTheme, type ThemeCheck, type TokenReader } from './guard.ts';
import { deriveOrgAccentOverride } from './orgAccent.ts';
import type { Accent } from './palette.ts';
import {
  type AccentSource,
  type ColorMode,
  type ThemePreference,
  themeAttributes,
  writePreference,
} from './preference.ts';
import { ThemeContext, type ThemeContextValue } from './themeContext.ts';

/** The `--s-*` custom properties an org accent override writes directly, bypassing `data-accent`'s
 * closed-enum blocks in `tokens.css` — exactly the tokens `guard.ts`'s `REQUIRED_PAIRS` already
 * checks for the closed-enum accent, reused here rather than a second list, since the same pairing
 * has to clear AA regardless of where the colour came from. `--s-focus` is included because
 * `tokens.css` derives it from the accent's `text` swatch (`--s-focus: var(--p-accent-text-l)`) —
 * an org override that left the focus ring on the old accent while every other accent token moved
 * would be a visible seam, not a safety issue, but still wrong. */
const ORG_OVERRIDE_TOKENS = [
  '--s-accent',
  '--s-accent-ink',
  '--s-accent-text',
  '--s-accent-soft',
  '--s-focus',
] as const;

function clearOrgOverride(root: HTMLElement): void {
  for (const token of ORG_OVERRIDE_TOKENS) root.style.removeProperty(token);
}

function applyOrgOverride(root: HTMLElement, brandColor: string): boolean {
  const swatch = deriveOrgAccentOverride(brandColor);
  if (swatch === null) {
    clearOrgOverride(root);
    return false;
  }
  root.style.setProperty('--s-accent', swatch.solid);
  root.style.setProperty('--s-accent-ink', swatch.onSolid);
  root.style.setProperty('--s-accent-text', swatch.text);
  root.style.setProperty('--s-accent-soft', swatch.soft);
  root.style.setProperty('--s-focus', swatch.text);
  return true;
}

function readComputedToken(root: HTMLElement): TokenReader {
  const computed = window.getComputedStyle(root);
  return (token) => computed.getPropertyValue(token);
}

/**
 * Sets `data-theme`/`data-accent` for `preference`'s closed-enum half, and — when `resolveAccentSource`
 * says the practice's own colour should govern — layers an org accent override on top via inline
 * custom-property writes. Returns what the browser actually computed afterwards, exactly as the
 * original single-argument version did; the read is deliberately of computed values rather than the
 * declarations just written, for the reason that function's own header already gave (a `var()` chain
 * that resolves to nothing computes to an empty string).
 */
function applyToDocument(preference: ThemePreference, orgBranding: OrgBranding | null): ThemeCheck {
  const root = document.documentElement;
  const attributes = themeAttributes(preference);

  if (attributes.theme === null) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', attributes.theme);
  }
  root.setAttribute('data-accent', attributes.accent);

  const source = resolveAccentSource(preference.accentSource, orgBranding);
  if (source === 'org' && orgBranding?.brandColor !== null && orgBranding !== null) {
    applyOrgOverride(root, orgBranding.brandColor);
  } else {
    clearOrgOverride(root);
  }

  return verifyTheme(readComputedToken(root));
}

interface AccentAwareResult {
  readonly ok: boolean;
  /** `null` on a clean pass. Set either when the whole apply failed (closed-enum and all), or when
   * only the org override failed and this apply fell back to the closed-enum accent underneath it —
   * the two are told apart by `ok`: `ok: false` is the former, `ok: true` with a non-null
   * `rejection` is the latter. */
  readonly rejection: string | null;
}

/**
 * The orchestration `commit`/`setOrgBranding` below both use: apply `preference` (with `orgBranding`
 * layered on top per `resolveAccentSource`), and if that specific combination fails its contrast
 * check, retry with the org override forced off before giving up. This is what makes a bad practice
 * brand colour degrade to "the closed-enum accent, with a visible notice" rather than either
 * silently shipping an illegible UI or rolling back a member's own, unrelated colour-mode choice.
 *
 * The retry is safe to treat as authoritative rather than needing a check-the-checker step of its
 * own: `palette.test.ts` already proves every closed-enum accent × mode combination clears AA
 * independently of anything in this file, so "apply with no org override" cannot be the thing that
 * was broken.
 */
function applyAccentAware(
  preference: ThemePreference,
  orgBranding: OrgBranding | null,
): AccentAwareResult {
  const check = applyToDocument(preference, orgBranding);
  if (check.ok) return { ok: true, rejection: null };

  const usedOrgOverride =
    resolveAccentSource(preference.accentSource, orgBranding) === 'org' &&
    orgBranding?.brandColor !== null &&
    orgBranding !== null;
  if (usedOrgOverride) {
    const fallback = applyToDocument(preference, null);
    if (fallback.ok) {
      return { ok: true, rejection: describeThemeCheck(check) };
    }
  }
  return { ok: false, rejection: describeThemeCheck(check) };
}

export interface ThemeProviderProps {
  /**
   * The preference read from storage by `main.tsx`. Passed in rather than read here so that this
   * component has no side effect during render — reading `localStorage` in a lazy state
   * initialiser is impure, and the impurity belongs at the application's entry point.
   */
  readonly initialPreference: ThemePreference;
  readonly children: ReactNode;
}

export function ThemeProvider({ initialPreference, children }: ThemeProviderProps) {
  const [preference, setPreference] = useState<ThemePreference>(initialPreference);
  const [orgBranding, setOrgBrandingState] = useState<OrgBranding | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  /** The last preference known to have passed. What a rejected change is rolled back to. */
  const lastGood = useRef<ThemePreference>(initialPreference);
  /** Mirrors `orgBranding` state for `commit`, which is a stable `useCallback` with no dependency
   * array entry for state that changes — matching `lastGood`'s own reasoning one field over. */
  const orgBrandingRef = useRef<OrgBranding | null>(null);
  const prefersDark = usePrefersDark();

  // Normalise what the inline bootstrap in `index.html` wrote. That script copies storage without
  // consulting the allowlist (deliberately — a second copy of the list would drift), so the first
  // authoritative apply happens here. Also re-applies whenever `orgBranding` itself changes (set by
  // `OrgBrandingSync.tsx` once a session exists), so a practice's colour reaches the document the
  // moment it is known, with no user action required.
  //
  // **Uses `applyAccentAware`, not the raw `applyToDocument`, and this is load-bearing rather than
  // a stylistic match with `commit`/`setOrgBranding` below.** A version of this effect that called
  // `applyToDocument` directly was caught, live, re-introducing a contrast-failing org colour that
  // `setOrgBranding` had *just* rejected and fallen back from: `setOrgBranding` calls
  // `applyAccentAware` and correctly leaves the DOM on the closed-enum fallback, but then updates
  // `orgBranding` state regardless (the settings page still needs to show what the practice actually
  // has stored, bad colour and all) — and that state change is exactly what re-runs *this* effect.
  // An unguarded re-apply at that point puts the rejected colour straight back onto `<html>`, one
  // render after the guard removed it. `applyAccentAware` here makes the effect idempotent with what
  // the imperative callers already established, instead of a second, unguarded path to the same DOM.
  useEffect(() => {
    applyAccentAware(preference, orgBranding);
  }, [preference, orgBranding]);

  const commit = useCallback((next: ThemePreference) => {
    const result = applyAccentAware(next, orgBrandingRef.current);
    if (!result.ok) {
      // Roll back, and do **not** persist. A rejected theme that survived a reload could lock a
      // practice out of a legible chart until someone with database access intervened.
      applyAccentAware(lastGood.current, orgBrandingRef.current);
      setRejection(result.rejection);
      return;
    }
    lastGood.current = next;
    writePreference(window.localStorage, next);
    setPreference(next);
    setRejection(result.rejection);
  }, []);

  const setColorMode = useCallback(
    (mode: ColorMode) => {
      commit({
        mode,
        accent: lastGood.current.accent,
        accentSource: lastGood.current.accentSource,
      });
    },
    [commit],
  );

  const setAccent = useCallback(
    (accent: Accent) => {
      const accentSource: AccentSource = 'personal';
      commit({ mode: lastGood.current.mode, accent, accentSource });
    },
    [commit],
  );

  const deferToOrgAccent = useCallback(() => {
    commit({
      mode: lastGood.current.mode,
      accent: lastGood.current.accent,
      accentSource: 'org',
    });
  }, [commit]);

  const setOrgBranding = useCallback((branding: OrgBranding | null) => {
    const result = applyAccentAware(lastGood.current, branding);
    orgBrandingRef.current = branding;
    setOrgBrandingState(branding);
    setRejection(result.rejection);
  }, []);

  const dismissRejection = useCallback(() => {
    setRejection(null);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolvedMode:
        preference.mode === 'system' ? (prefersDark ? 'dark' : 'light') : preference.mode,
      orgBranding,
      resolvedAccentSource: resolveAccentSource(preference.accentSource, orgBranding),
      setColorMode,
      setAccent,
      deferToOrgAccent,
      setOrgBranding,
      rejection,
      dismissRejection,
    }),
    [
      preference,
      prefersDark,
      orgBranding,
      setColorMode,
      setAccent,
      deferToOrgAccent,
      setOrgBranding,
      rejection,
      dismissRejection,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
